import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {XSearchComponent} from "../../../x-kit/components/x-search/x-search.component";
import {provideComponentStore} from "@ngrx/component-store";
import {SearchStore} from "../../data-access/search-store";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {XSearchListItemComponent} from "../../../x-kit/components/x-search-list-item/x-search-list-item.component";
import {XSearchListTitleComponent} from "../../../x-kit/components/x-search-list-title/x-search-list-title.component";
import {XDividerComponent} from "../../../x-kit/components/x-divider/x-divider.component";
import {XMenuComponent} from "../../../x-kit/components/x-menu/x-menu.component";
import {XAutocompleteComponent} from "../../../x-kit/components/x-autocomplete/x-autocomplete.component";
import {SearchDictsService} from "../../data-access/search-dicts.service";
import {isSearchPlotItem, MapboxResult, SearchApiItem, SearchResult} from "../../data-access/search.service";
import {SearchMapIntegrationService} from "../../data-access/search-map-integration.service";
import {TranslatePipe} from "@ngx-translate/core";
import {MetricService} from "../../../../core/services/metric.service";
import {UserJourneyEvent} from "../../../../core/services/user-journey.types";

@Component({
  selector: 'app-search-feature',
  standalone: true,
  imports: [
    XSearchComponent,
    AsyncPipe,
    NgIf,
    NgForOf,
    XSearchListItemComponent,
    XSearchListTitleComponent,
    XDividerComponent,
    XMenuComponent,
    XAutocompleteComponent,
    TranslatePipe,
  ],
  templateUrl: './search-feature.component.html',
  styleUrl: './search-feature.component.less',
  providers: [provideComponentStore(SearchStore)],
})
export class SearchFeatureComponent {
  @Input() isMobile: boolean = false;
  @Input() withSearchButton: boolean = false;
  @Input() placeholder = 'Type to search...';
  /**
   * Special mode (used by valuation room creation):
   * if Perare results contain plot items (with uuid), show ONLY those plot items.
   * Helps user pick a parcel uuid and avoids selecting admin areas/places.
   */
  @Input() uuidResultsOnlyMode: boolean = false;

  @Output() buttonSearchClick = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<SearchResult<SearchApiItem> | MapboxResult>();

  public searchStore = inject(SearchStore);
  public searchTemp = inject(SearchDictsService);
  private searchMapInt = inject(SearchMapIntegrationService);
  private metricService = inject(MetricService);

  private selectedPerareKey: string | null = null;
  private selectedMapboxKey: string | null = null;

  public onPerareResultClick(result: SearchResult<SearchApiItem>) {
    this.selectedPerareKey = this.getPerareResultKey(result);
    this.selectedMapboxKey = null;
    this.itemSelected.emit(result);
    this.searchMapInt.requestFlyToToSearchItem(result);
  }

  public onMapboxResultClick(result: MapboxResult) {
    this.selectedMapboxKey = this.getMapboxResultKey(result);
    this.selectedPerareKey = null;
    this.itemSelected.emit(result);
    this.searchMapInt.requestFlyToMapboxItem(result);
  }

  public getPerareResultsForDisplay(results: SearchResult<SearchApiItem>[]): SearchResult<SearchApiItem>[] {
    if (!Array.isArray(results)) return [];
    if (!this.uuidResultsOnlyMode) return results;

    const uuidResults = results.filter((r) => isSearchPlotItem(r.data as any));
    return uuidResults.length ? uuidResults : results;
  }

  public shouldHideMapboxResults(perareResults: SearchResult<SearchApiItem>[]): boolean {
    if (!this.uuidResultsOnlyMode) return false;
    if (!Array.isArray(perareResults)) return false;
    return perareResults.some((r) => isSearchPlotItem(r.data as any));
  }

  protected readonly sea = module;

  public isPerareResultSelected(result: SearchResult<SearchApiItem>): boolean {
    const key = this.getPerareResultKey(result);
    return !!this.selectedPerareKey && key === this.selectedPerareKey;
  }

  public isMapboxResultSelected(result: MapboxResult): boolean {
    const key = this.getMapboxResultKey(result);
    return !!this.selectedMapboxKey && key === this.selectedMapboxKey;
  }

  private getPerareResultKey(result: SearchResult<SearchApiItem>): string {
    const data: any = (result as any)?.data;
    if (isSearchPlotItem(data) && typeof data.uuid === 'string') {
      return `plot:${data.uuid}`;
    }
    if (data?.type === 'coors') {
      return `coors:${data?.x ?? ''},${data?.y ?? ''}:${data?.label ?? ''}`;
    }
    if (data?.zoneId) {
      return `${data?.type ?? 'zone'}:${data.zoneId}`;
    }
    return `res:${result?.title ?? ''}:${result?.description ?? ''}`;
  }

  private getMapboxResultKey(result: MapboxResult): string {
    const r: any = result as any;
    return String(r?.id ?? r?.place_name ?? r?.text ?? '');
  }
}
